import React, { useMemo, useState } from 'react';
import {
    ShoppingBag, MessageSquare, AlertTriangle, Circle, Truck, IndianRupee,
    Stethoscope, Phone, CalendarClock, StickyNote, CheckCircle2, Users, Gift,
} from 'lucide-react';
import { formatDate } from '../../../shared/utils/formatters';
import { TIMELINE_CATEGORIES, buildTimelineRows, hasStoredTimeline } from '../logic/timelineEvents';

const CATEGORY_STYLE = {
    offers: { icon: Gift, color: 'bg-emerald-600' },
    orders: { icon: ShoppingBag, color: 'bg-blue-500' },
    courier: { icon: Truck, color: 'bg-orange-500' },
    complaints: { icon: AlertTriangle, color: 'bg-red-500' },
    doctor: { icon: Stethoscope, color: 'bg-purple-500' },
    refund: { icon: IndianRupee, color: 'bg-cyan-600' },
    communication: { icon: MessageSquare, color: 'bg-emerald-500' },
    followup: { icon: CalendarClock, color: 'bg-amber-500' },
    notes: { icon: StickyNote, color: 'bg-slate-400' },
    other: { icon: Circle, color: 'bg-slate-300' },
};

const fmt = (ts) => formatDate(typeof ts === 'number' ? new Date(ts).toISOString() : ts);

const TimelineItem = ({ row, resolveName }) => {
    const style = CATEGORY_STYLE[row.category] || CATEGORY_STYLE.other;
    const Icon = style.icon;
    return (
        <div className="flex gap-4 relative">
            <div className="absolute left-3.5 top-8 bottom-0 w-0.5 bg-slate-100 -z-10"></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${style.color}`}>
                <Icon size={14} className="text-white" />
            </div>
            <div className="pb-8 min-w-0">
                <p className="text-xs text-slate-400 mb-1">{fmt(row.ts)}</p>
                <p className="font-bold text-slate-800 text-sm flex items-center gap-2 flex-wrap">
                    {row.title}
                    {row.status && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 uppercase">{row.status}</span>}
                    {row.channel && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 uppercase">{row.channel}</span>}
                </p>
                {row.description && <p className="text-sm text-slate-600 mt-1 break-words">{row.description}</p>}
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-400">
                    {row.orderId && <span>Order: {row.orderId}</span>}
                    {row.ticketNumber && <span>Ticket: {row.ticketNumber}</span>}
                    {row.product && <span>Product: {row.product}</span>}
                    {row.createdBy && <span>By: {resolveName ? resolveName(row.createdBy) : row.createdBy}</span>}
                </div>
            </div>
        </div>
    );
};

// Build legacy display rows (used only when there is no stored timeline, except
// remarks/referrals which never appear in the stored timeline so can't duplicate).
const legacyOrderRows = (orders) => (Array.isArray(orders) ? orders : Object.values(orders || {})).map(o => ({
    category: 'orders', title: `Order Placed #${o.orderId || o.id || 'Unknown'}`,
    description: `${o.productName || o.productSummary || o.productCode || 'Product'} - ₹${o.amount || o.price || 0}${o.status && o.status !== 'Pending' ? ` · ${String(o.status).toUpperCase()}` : ''}`,
    orderId: o.orderId || o.id || '', product: o.productName || '', status: '', channel: '', ticketNumber: '', createdBy: '',
    ts: new Date(o.orderDate || o.createdAt || 0).getTime(),
}));
const remarkRows = (remarks) => (Array.isArray(remarks) ? remarks : Object.values(remarks || {})).map(r => ({
    category: 'notes', title: 'Remark Added', description: typeof r.text === 'string' ? r.text : 'Note',
    orderId: '', product: '', status: '', channel: '', ticketNumber: '', createdBy: r.author || '',
    ts: new Date(r.createdAt || 0).getTime(),
}));
const legacyComplaintRows = (complaints) => (Object.values(complaints || {})).map(c => ({
    category: 'complaints', title: `Complaint: ${c.category || 'Issue'}`, description: typeof c.details === 'string' ? c.details : 'Details',
    orderId: '', product: '', status: c.status || '', channel: '', ticketNumber: '', createdBy: '',
    ts: new Date(c.createdAt || 0).getTime(),
}));
const referralRows = (referrals) => (Array.isArray(referrals) ? referrals : Object.values(referrals || {})).map(r => ({
    category: 'orders', title: `New Referral: ${r.customerName || 'Customer'}`,
    description: `Order Total: ₹${r.orderTotal || 0} · Reward: ₹${r.referralAmount || 0}`,
    orderId: '', product: '', status: '', channel: '', ticketNumber: '', createdBy: '', ts: new Date(r.createdAt || 0).getTime(),
}));

const PAGE = 15;   // one screenful; the rest loads on demand

/**
 * Turn reward-ledger movements into timeline rows.
 *
 * A credit given by hand or by a campaign is a thing that HAPPENED to this customer, so
 * it belongs on their timeline beside their orders and complaints — not only inside the
 * wallet card. Holds and releases are left out: a reservation that came and went is
 * plumbing, not history.
 */
const rewardRowsFrom = (rows = []) => (rows || [])
    .filter(r => ['credit', 'redeem', 'expire', 'restore', 'reverse'].includes(r?.type))
    .map(r => {
        const pts = Math.max(0, Math.trunc(Number(r.points) || 0));
        const sign = ['redeem', 'expire', 'reverse'].includes(r.type) ? '-' : '+';
        return {
            id: `rw_${r.id || r.at}`,
            ts: Number(r.at) || 0,
            category: 'rewards',
            type: `reward_${r.type}`,
            title: {
                credit: 'Reward Points Credited',
                redeem: 'Reward Points Used',
                expire: 'Reward Points Expired',
                restore: 'Reward Points Returned',
                reverse: 'Reward Points Reversed',
            }[r.type],
            detail: [
                `${sign}${pts} point${pts === 1 ? '' : 's'}`,
                r.narration || r.reason || '',
                r.orderId || r.orderRef ? `Order ${r.orderId || r.orderRef}` : '',
                r.campaign ? `Campaign ${r.campaign}` : '',
                r.actor && r.actor !== 'system' ? `by ${r.actor}` : '',
            ].filter(Boolean).join(' · '),
        };
    });

const CustomerTimeline = ({ timeline, orders = [], remarks = [], complaints = {}, referrals = [], offerRows = [], rewardRows = [], hideCategories = [], resolveName }) => {
    const [filter, setFilter] = useState('all');
    const [shown, setShown] = useState(PAGE);   // a long-standing customer has hundreds of rows

    const rows = useMemo(() => {
        const stored = buildTimelineRows(timeline, orders);
        const useStored = hasStoredTimeline(timeline);
        // Orders + complaints come from stored when available (avoid duplicates);
        // remarks + referrals are never in stored, so always included.
        const legacy = [
            ...(useStored ? [] : legacyOrderRows(orders)),
            ...(useStored ? [] : legacyComplaintRows(complaints)),
            ...remarkRows(remarks),
            ...referralRows(referrals),
        ];
        const hidden = new Set(hideCategories);
        return [...stored, ...legacy, ...(offerRows || []), ...rewardRowsFrom(rewardRows)].filter(r => !hidden.has(r.category)).sort((a, b) => b.ts - a.ts);
    }, [timeline, orders, remarks, complaints, referrals, offerRows, rewardRows, hideCategories]);

    const counts = useMemo(() => {
        const c = { all: rows.length };
        rows.forEach(r => { c[r.category] = (c[r.category] || 0) + 1; });
        return c;
    }, [rows]);

    const matching = filter === 'all' ? rows : rows.filter(r => r.category === filter);
    const visible = matching.slice(0, shown);

    // Switching tab starts the new list from the top rather than keeping a stale depth.
    const pick = (key) => { setFilter(key); setShown(PAGE); };

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <h3 className="font-bold text-slate-800 mb-4">Customer Timeline</h3>

            <div className="flex flex-wrap gap-1.5 mb-5">
                {Object.entries(TIMELINE_CATEGORIES).map(([key, label]) => (
                    (key === 'all' || counts[key]) ? (
                        <button key={key} onClick={() => pick(key)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${filter === key ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                            {label}{counts[key] ? ` (${counts[key]})` : ''}
                        </button>
                    ) : null
                ))}
            </div>

            <div className="space-y-0">
                {visible.length === 0 ? (
                    <p className="text-slate-400 text-sm text-center py-4">No activity recorded.</p>
                ) : (
                    visible.map((row, i) => <TimelineItem key={i} row={row} resolveName={resolveName} />)
                )}
            </div>

            {matching.length > visible.length && (
                <div className="pt-3 mt-1 border-t border-slate-100 flex items-center gap-3">
                    <span className="text-xs text-slate-400">Showing {visible.length} of {matching.length}</span>
                    <button onClick={() => setShown(n => n + PAGE)}
                        className="ml-auto px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-700">
                        Show {Math.min(PAGE, matching.length - visible.length)} more
                    </button>
                    <button onClick={() => setShown(matching.length)}
                        className="text-xs font-bold text-purple-600 hover:text-purple-700">Show all</button>
                </div>
            )}
        </div>
    );
};

export default CustomerTimeline;
